import React from 'react';
import { currencyFormatter } from 'utils/functions';

import { nonNegativeAmount, ordinal, PayoutRow } from './payouts';

import './TournamentPayouts.scss';

type Props = {
  rows: PayoutRow[];
  status: 'loading' | 'ready' | 'unavailable';
  retry: () => void;
  seats?: number;
  bountyAmount?: number;
  variant?: 'registration' | 'info';
};

export default function TournamentPayouts({
  rows,
  status,
  retry,
  seats,
  bountyAmount,
  variant = 'registration'
}: Props) {
  const paid = rows.reduce((count, row) => count + row.to - row.from + 1, 0);
  const winnerTakesAll = paid === 1 && rows[0]?.from === 1;
  const bounty = rows[0]?.bountyAmount ?? nonNegativeAmount(bountyAmount);
  const seatLabel =
    Number.isSafeInteger(seats) && Number(seats) > 0
      ? seats === 2
        ? 'Heads-up Sit & Go'
        : `${seats}-seat Sit & Go`
      : 'Sit & Go';
  return (
    <section
      className={`tournament-payouts tournament-payouts--${variant}`}
      aria-label='Payout structure'>
      <div className='tournament-payouts__heading'>
        <div>
          <h3>{winnerTakesAll ? 'Winner takes all' : 'Payout structure'}</h3>
          <p>{seatLabel}</p>
        </div>
        {status === 'ready' && (
          <span className='tournament-payouts__pill'>
            {paid} {paid === 1 ? 'place' : 'places'} paid
          </span>
        )}
      </div>
      {status === 'ready' ? (
        <table>
          <thead>
            <tr>
              <th scope='col'>Place</th>
              <th scope='col'>Payout</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.rank}>
                <td>
                  {row.from === row.to
                    ? `${ordinal(row.from)} place`
                    : `${ordinal(row.from)}–${ordinal(row.to)} places`}
                </td>
                <td>
                  {row.isTicket ? 'Ticket' : currencyFormatter(row.amount)}
                  {row.to > row.from ? ' each' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className='tournament-payouts__empty' role='status'>
          {status === 'loading' ? (
            'Loading payouts…'
          ) : (
            <>
              Payout details unavailable.{' '}
              <button type='button' onClick={retry}>
                Try again
              </button>
            </>
          )}
        </div>
      )}
      {bounty !== undefined && bounty > 0 && (
        <div className='tournament-payouts__bounty'>
          <strong>Knockout rewards</strong>
          <p>{currencyFormatter(bounty)} per knockout</p>
        </div>
      )}
    </section>
  );
}
